import applyToSelectedMediaTypeOperation from 'editor/src/store/design/operation/applyToSelectedMediaTypeOperation';
import { MediaText, TextWrapping } from 'editor/src/store/design/types';
import { setCurrentStyleAction } from 'editor/src/store/editor/slice';
import { FontStyles } from 'editor/src/store/fonts/types';
import isFontFileLoaded from 'editor/src/store/fonts/utils/isFontFileLoaded';
import type { ThunkDispatch } from 'editor/src/store/hooks';

import { logEvent } from 'editor/src/amplitude';
import isShadowEmpty from 'editor/src/util/isShadowEmpty';

import { extractTextProperties } from 'editor/src/component/EditorArea/Spread/Page/MediaElement/Text/updateTextElementWithoutRender';

interface FontStylesWithMultipleSizes extends Omit<Partial<FontStyles>, 'fontSize'> {
  fontSize?: number | ((currentSize: number) => number);
}

const applyFontStylesToSelectionOperation =
  ({ color, angle, ...extra }: FontStylesWithMultipleSizes, preventSavingState = false) =>
  (dispatch: ThunkDispatch) => {
    // Allow protected uploaded fonts that have no file to load — they render via server-side digitization
    if (extra.fontFamily && !isFontFileLoaded(extra.fontFamily) && !extra.fontFamily.startsWith('custom-')) {
      throw new Error(`Tried to apply a font which has not been loaded - ${extra.fontFamily}`);
    }

    dispatch(
      applyToSelectedMediaTypeOperation(
        'text',
        (element) => {
          const textElementUpdate: Partial<MediaText> = {
            fill: color ?? element.fill,
            r: angle ?? element.r,
            extra: {
              ...element.extra,
              ...extra,
              fontSize:
                typeof extra.fontSize === 'function'
                  ? extra.fontSize(element.extra.fontSize)
                  : extra.fontSize ?? element.extra.fontSize,
            },
          };

          if (extra.fontFamily) {
            dispatch(
              setCurrentStyleAction({
                type: 'text',
                update: { fontFamily: extra.fontFamily },
              }),
            );
          }

          if (extra.letterSpacing) {
            logEvent('element modified', {
              type: 'text',
              property: 'letter-spacing',
              value: extra.letterSpacing,
            });
          }

          if ('shadow' in extra) {
            logEvent('element modified', {
              type: 'text',
              property: 'shadow',
              value: !isShadowEmpty(extra.shadow),
            });
            dispatch(
              setCurrentStyleAction({
                type: 'text',
                update: { shadow: extra.shadow ?? element.extra.shadow },
              }),
            );
          }

          if ('stroke' in extra) {
            logEvent('element modified', {
              type: 'text',
              property: 'stroke',
              value: !!extra.stroke,
            });
            dispatch(
              setCurrentStyleAction({
                type: 'text',
                update: { stroke: extra.stroke ?? element.extra.stroke },
              }),
            );
          }

          // Clear server-generated SVG path when font changes — new font needs fresh rendering
          if (extra.fontFamily && extra.fontFamily !== element.extra.fontFamily) {
            textElementUpdate.path = undefined;
          }

          const newElement = { ...element, ...textElementUpdate };

          // Skip dimension recalculation for protected custom fonts — font isn't loaded,
          // fallback font would produce wrong dimensions. The server-returned SVG path
          // defines the correct layout for the current element dimensions.
          const isProtectedCustomFont = extra.fontFamily?.startsWith('custom-') && !isFontFileLoaded(extra.fontFamily);

          if (
            !isProtectedCustomFont &&
            newElement.extra.wrapping !== TextWrapping.Fit &&
            newElement.extra.wrapping !== TextWrapping.FitOneLine
          ) {
            const recalculateTextWidth = 'curve' in extra;
            const { width, height, sx, sy, x, y } = extractTextProperties(
              { ...element, ...textElementUpdate },
              undefined,
              recalculateTextWidth,
            );
            textElementUpdate.width = width;
            textElementUpdate.height = height;
            textElementUpdate.sx = sx;
            textElementUpdate.sy = sy;
            textElementUpdate.x = x;
            textElementUpdate.y = y;
          }

          return textElementUpdate;
        },
        preventSavingState,
      ),
    );
  };

export default applyFontStylesToSelectionOperation;
